import React from "react";
import { useInfiniteContacts } from "@/hooks/useInfiniteContacts";
import { useAuth } from "@/contexts/AuthContext";
import {
  getDisplayName,
  getContactPhone,
  formatCreatedDate,
} from "@/utils/contactUtils";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import { Switch } from "@/components/ui/switch";
import ContactEdit from "./ContactEdit";
import { Badge } from "@/components/ui/badge";
import { useAssistants } from "@/hooks/useAssistants";
import { useTeam } from "@/hooks/useTeam";
import { useState, useMemo } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import type { ContactFilters } from "@/components/ui/shared/MoreFilter";
import type { SortOption } from "@/components/contatos/FilterHeader";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ContactsTableProps {
  searchTerm?: string;
  filters?: ContactFilters;
  sortBy?: SortOption;
}

const ContactsTable: React.FC<ContactsTableProps> = ({
  searchTerm = "",
  filters,
  sortBy = "created_desc",
}) => {
  const { t } = useTranslation("crm");
  const {
    contacts,
    initialLoading,
    error,
    refetch,
    updateContact,
    hasMore,
    loadMore,
    totalCount,
  } = useInfiniteContacts({ searchTerm });
  const { user } = useAuth();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredContacts = useMemo(() => {
    let filtered = [...contacts];

    if (filters) {
      if (filters.assistantId && filters.assistantId !== "__clear__") {
        filtered = filtered.filter(
          (contact) => contact.account_assistant_id === filters.assistantId
        );
      }

      if (filters.attributionId && filters.attributionId !== "__clear__") {
        if (filters.attributionId === "__unassigned__") {
          filtered = filtered.filter(
            (contact) =>
              contact.attribution_id === null ||
              contact.attribution_id === undefined
          );
        } else {
          filtered = filtered.filter(
            (contact) => contact.attribution_id === filters.attributionId
          );
        }
      }

      if (filters.tags.length > 0 && filters.tags[0] !== "__clear__") {
        filtered = filtered.filter((contact) => {
          const contactTags = (contact as any).tags;
          if (!Array.isArray(contactTags) || contactTags.length === 0)
            return false;
          return filters.tags.some((filterTag) =>
            contactTags.includes(filterTag)
          );
        });
      }

      if (filters.campaigns.length > 0 && filters.campaigns[0] !== "__clear__") {
        filtered = filtered.filter((contact) => {
          const contactCampaigns = (contact as any).campaigns;
          if (!Array.isArray(contactCampaigns) || contactCampaigns.length === 0)
            return false;
          return filters.campaigns.some((filterCampaign) =>
            contactCampaigns.includes(filterCampaign)
          );
        });
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        filtered = filtered.filter((contact) => {
          if (!contact.created_at) return false;
          const contactDate = new Date(contact.created_at);
          contactDate.setHours(0, 0, 0, 0);
          return contactDate >= fromDate;
        });
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter((contact) => {
          if (!contact.created_at) return false;
          const contactDate = new Date(contact.created_at);
          return contactDate <= toDate;
        });
      }
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "created_desc":
          return (
            new Date(b.created_at || 0).getTime() -
            new Date(a.created_at || 0).getTime()
          );
        case "created_asc":
          return (
            new Date(a.created_at || 0).getTime() -
            new Date(b.created_at || 0).getTime()
          );
        case "name_asc":
          return getDisplayName(a).localeCompare(getDisplayName(b));
        case "name_desc":
          return getDisplayName(b).localeCompare(getDisplayName(a));
        default:
          return 0;
      }
    });

    return filtered;
  }, [contacts, filters, sortBy]);

  const handleAssistantToggle = async (
    contactId: string,
    _currentAssistantId: string | null
  ) => {
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact || !user) return;

    const newStatus = !contact.assistant_is_active;

    try {
      const success = await updateContact(contactId, {
        assistant_is_active: newStatus,
      });

      if (!success) {
        setLocalError("Erro ao atualizar status do assistente");
        return;
      }

      setLocalError(null);
    } catch (err) {
      setLocalError(
        `Erro inesperado: ${err instanceof Error ? err.message : "Erro desconhecido"}`
      );
    }
  };

  const emptyCard =
    "w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm";

  if (initialLoading) {
    return (
      <div className="flex-1 min-h-0">
        <div className={emptyCard}>
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" />
            <p className="text-muted-foreground">{t("list.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  const displayError = error || localError;

  if (displayError) {
    return (
      <div className="flex-1 min-h-0">
        <div className={emptyCard}>
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t("list.loadError")}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {displayError}
              </p>
              <button
                onClick={refetch}
                className="px-4 py-2 h-10 rounded-[12px] bg-primary hover:bg-primary-hover text-primary-foreground transition-colors duration-200"
              >
                {t("list.retry")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <div className={emptyCard}>
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="w-16 h-16 bg-card-muted rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-muted-foreground"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t("list.empty")}
              </h3>
              <p className="text-muted-foreground text-sm">
                {t("list.emptyHint")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters =
    filters &&
    (filters.assistantId ||
      filters.attributionId ||
      filters.tags.length > 0 ||
      filters.campaigns.length > 0 ||
      filters.dateFrom ||
      filters.dateTo);

  if (filteredContacts.length === 0) {
    return (
      <div className="flex-1 min-h-0">
        <div className={emptyCard}>
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {t("list.empty")}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {searchTerm.trim()
                  ? `Nenhum contato corresponde à busca "${searchTerm}".`
                  : hasActiveFilters
                    ? "Tente ajustar ou limpar os filtros para ver mais resultados."
                    : "Aguardando contatos..."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const shouldLoadMore = !hasActiveFilters && hasMore;

  return (
    <div className="flex-1 min-h-0">
      <div
        id="contacts-scroll-container"
        className="w-full max-h-full overflow-auto border border-border rounded-[20px] bg-card shadow-sm table-scrollbar"
        style={{ height: "calc(100vh - 300px)" }}
      >
        <InfiniteScroll
          dataLength={filteredContacts.length}
          next={loadMore}
          hasMore={shouldLoadMore}
          loader={
            <div className="flex justify-center items-center py-8">
              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 border-3 border-border border-t-primary rounded-full animate-spin" />
                <p className="text-muted-foreground text-sm">
                  {t("list.loadingMore")}
                </p>
              </div>
            </div>
          }
          endMessage={
            <div className="flex justify-center items-center py-8">
              <div className="flex flex-col items-center gap-2">
                <CheckCircle2 className="w-8 h-8 text-muted-foreground/60" />
                <p className="text-muted-foreground text-sm font-medium">
                  {hasActiveFilters
                    ? t("contacts.filteredResults")
                    : t("contacts.allLoaded")}
                </p>
                <p className="text-muted-foreground text-xs">
                  {hasActiveFilters
                    ? t("contacts.countFiltered", { shown: filteredContacts.length, total: totalCount })
                    : t("contacts.countTotal", { total: totalCount })}
                </p>
              </div>
            </div>
          }
          scrollableTarget="contacts-scroll-container"
        >
          <Table className="min-w-[1200px]">
            <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
              <TableRow>
                {[
                  t("contacts.thContact"),
                  t("contacts.thPhone"),
                  t("contacts.thTags"),
                  t("contacts.thCreatedAt"),
                  t("contacts.thAssigned"),
                  t("contacts.thAssistant"),
                  t("contacts.thAssistantOn"),
                  t("contacts.thActions"),
                ].map((item, index) => (
                  <TableCell
                    key={index}
                    as="th"
                    className="!text-[#475569] !font-semibold !text-[13px] !py-4"
                  >
                    <div className="flex justify-start items-center gap-1">
                      <p>{item}</p>
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContacts.map((contact) => {
                const displayName = getDisplayName(contact);
                const contactPhone = getContactPhone(contact);
                const createdDate = formatCreatedDate(contact.created_at);
                const assignedAssistant = assistants.find(
                  (a) => a.id === contact.account_assistant_id
                );
                const attributedMember = contact.attribution_id
                  ? members.find((m) => m.auth_user_id === contact.attribution_id)
                  : undefined;

                const contactTags = (contact as any).tags || [];
                const visibleTags = contactTags.slice(0, 2);
                const remainingCount = contactTags.length - 2;

                return (
                  <TableRow
                    key={contact.id}
                    className="hover:bg-[#F8FAFC] dark:hover:bg-hover transition-colors duration-200 border-b border-border/60 last:border-b-0"
                  >
                    <TableCell as="td" className="!py-4">
                      <div className="flex flex-col gap-0.5">
                        <p className="font-semibold text-foreground">
                          {displayName}
                        </p>
                        {contact.is_group && (
                          <Badge variant="default2" className="text-xs w-fit mt-1">
                            {t("list.group")}
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    <TableCell
                      as="td"
                      className="!py-4 !text-[#334155] dark:!text-foreground"
                    >
                      {contactPhone}
                    </TableCell>

                    <TableCell as="td" className="!py-4">
                      {contactTags.length === 0 ? (
                        <span className="text-muted-foreground text-sm">-</span>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap max-w-[200px]">
                          {visibleTags.map((tag: string, idx: number) => (
                            <Badge key={idx} variant="tag" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {remainingCount > 0 && (
                            <Badge variant="tag" className="text-xs">
                              +{remainingCount}
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>

                    <TableCell
                      as="td"
                      className="!py-4 !text-[#334155] dark:!text-foreground"
                    >
                      {createdDate}
                    </TableCell>

                    <TableCell as="td" className="!py-4">
                      {attributedMember ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-foreground text-sm font-medium">
                            {attributedMember.name}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {attributedMember.email}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>

                    <TableCell as="td" className="!py-4">
                      {assignedAssistant ? (
                        <span className="text-foreground text-sm font-medium truncate block max-w-[180px]">
                          {assignedAssistant.name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">
                          {t("common.noAssistant")}
                        </span>
                      )}
                    </TableCell>

                    <TableCell as="td" className="!py-4">
                      <Switch
                        checked={contact.assistant_is_active || false}
                        onCheckedChange={() =>
                          handleAssistantToggle(
                            contact.id,
                            contact.account_assistant_id
                          )
                        }
                      />
                    </TableCell>

                    <TableCell as="td" className="!py-4">
                      <ContactEdit
                        contact={contact}
                        onContactDeleted={refetch}
                        onContactUpdated={refetch}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default ContactsTable;
